import React, { useState, useEffect, useMemo } from "react";
import { FaStar, FaCamera, FaTimes } from "react-icons/fa";
import axios from "axios";
import "./ClubReviewForm.css";
import { API_BASE_URL } from "../../utils";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGeoLocation } from "../../context/LocationContext";
import { RiArrowDownWideLine } from "react-icons/ri";

const url = `${API_BASE_URL}/master/assets/uploads/`;

export default function ClubReviewForm({ isVenue }) {
  const navigate = useNavigate();
  const { clubID } = useParams();
  const [searchParams] = useSearchParams();
  const typeProduct = searchParams.get("type");
  const order_id = searchParams.get("order_id");
  const { latitude, longitude } = useGeoLocation();

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [criteriaRatings, setCriteriaRatings] = useState({});
  const [reviewText, setReviewText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [parameter, setParameter] = useState([]);
  const [responseMessage, setResponseMessage] = useState("");
  const [userdata] = useState(JSON.parse(localStorage.getItem("userData")));
  const [clubDetail, setClubDetail] = useState({});
  const [showCriteria, setShowCriteria] = useState(false);

  // 🟣 Load parameters (Food, Ambience, etc.)
  useEffect(() => {
    axios.get(`${API_BASE_URL}/master/Api/getData/Parameter`).then((res) => {
      if (res.data?.data) {
        setParameter(res.data.data);
      }
    });
  }, []);

  // 🟣 Calculate average rating dynamically
  const averageRating = useMemo(() => {
    const values = Object.values(criteriaRatings).filter((v) => v > 0);
    if (values.length === 0) return 0;
    return values.reduce((a, b) => a + b, 0) / values.length;
  }, [criteriaRatings]);

  // 🟣 When user clicks overall stars → set all criteria ratings
  const handleOverallClick = (value) => {
    setRating(value);
    const updated = {};
    parameter.forEach((param) => {
      updated[param.name] = value;
    });
    setCriteriaRatings(updated);
  };

  // 🟣 When user updates any criteria → recalc average
  useEffect(() => {
    const avg = averageRating;
    if (avg > 0) {
      setRating(Number(avg.toFixed(1)));
    }
  }, [averageRating]);

  // 🟣 Handle individual criteria clicks
  const handleCriteriaRating = (item, value) => {
    setCriteriaRatings((prev) => ({ ...prev, [item]: value }));
  };

  // 🟣 Handle photo uploads
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const urls = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos((prev) => [...prev, ...urls]);
  };

  const removePhoto = (url) => {
    setPhotos((prev) => prev.filter((p) => p.preview !== url));
  };

  // 🟣 Submit review
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userdata?.id) {
      setResponseMessage("Please login first.");
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("club_id", clubID?.id || clubID);
      formDataToSend.append("user_id", userdata.id);
      formDataToSend.append("overall_rating", rating);
      formDataToSend.append("msg", reviewText);
      formDataToSend.append("isdetailpage", isVenue ? "yes" : "no");
      formDataToSend.append("type", typeProduct || "");
      formDataToSend.append("order_id", order_id || "");

      // Criteria ratings
      parameter.forEach((param) => {
        formDataToSend.append("parameter_id[]", param.id);
        formDataToSend.append("stars[]", criteriaRatings[param.name] || 0);
      });

      // Photos
      photos.forEach((p) => {
        formDataToSend.append("image[]", p.file);
      });

      const response = await axios.post(
        `${API_BASE_URL}/master/Api/sendData/submitReview`,
        formDataToSend
      );

      if (response.data.status) {
        setResponseMessage("Review submitted successfully!");
        if (isVenue) {
          if (window.history.length > 2) navigate(-1);
          else navigate("/");
        } else {
          navigate(
            `/my-bookings/review/thank-you?code=${response?.data?.data?.code}`
          );
        }
        setRating(0);
        setCriteriaRatings({});
        setReviewText("");
        setPhotos([]);
      } else {
        setResponseMessage("Failed to submit review.");
      }
    } catch (error) {
      console.error(error);
      setResponseMessage("An error occurred while submitting.");
    }
  };

  return (
    <form className="club-review-form" onSubmit={handleSubmit}>
      {/* 👤 User Info */}
      <div className="club-review-user">
        <div className="club-review-user-info">
          <div className="club-review-avatar-wrapper">
            <img
              src={url + userdata?.img}
              alt="profile"
              className="club-review-avatar"
              onError={(e) => {
                e.target.src =
                  "https://img.freepik.com/premium-vector/vector-flat-illustration-grayscale-avatar-user-profile-person-icon-gender-neutral-silhouette-profile-picture-suitable-social-media-profiles-icons-screensavers-as-templatex9xa_719432-2210.jpg";
              }}
            />
          </div>
          <div className="club-review-user-name">
            <h4>{userdata?.name || "Guest User"}</h4>
            {/* <p>{new Date().toLocaleDateString()}</p> */}
          </div>
        </div>

        {/* ⭐ Overall Rating */}
        <div className="club-review-main-rating">
          <h6>Overall Rating</h6>
          <div className="club-review-stars-wrapper">
            {[...Array(5)].map((_, i) => {
              const ratingValue = i + 1;
              return (
                <FaStar
                  key={i}
                  size={22}
                  className={
                    ratingValue <= (hover || Math.round(rating))
                      ? "club-review-star active"
                      : "club-review-star"
                  }
                  onClick={() => handleOverallClick(ratingValue)}
                  onMouseEnter={() => setHover(ratingValue)}
                  onMouseLeave={() => setHover(0)}
                />
              );
            })}
          </div>
          {rating > 0 && (
            <span>{Number(rating).toFixed(1)} out of 5 stars selected</span>
          )}
        </div>
      </div>

      {/* 🔹 Criteria Ratings */}
      <div className="club-review-criteria">
        <p className="club-review-instruction">
          Please rate the key aspects below to understand your experience.
        </p>
        <span onClick={() => setShowCriteria(!showCriteria)} className="club-review-criteria-toggle">more criteria <RiArrowDownWideLine /> </span>
        {showCriteria && (
          <>
            <div className="club-review-criteria-list-wrapper">
              {parameter.map((param) => (
                <div key={param.id} className="club-review-criteria-item">
                  <span>{param.name}</span>
                  <div className="club-review-stars-wrapper">
                    {[...Array(5)].map((_, i) => {
                      const value = i + 1;
                      return (
                        <FaStar
                          key={i}
                          size={20}
                          className={
                            value <= (criteriaRatings[param.name] || 0)
                              ? "club-review-star active"
                              : "club-review-star"
                          }
                          onClick={() => handleCriteriaRating(param.name, value)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))
              }
            </div>
          </>
        )}

      </div>

      {/* 📝 Review Text */}
      <textarea
        className="club-review-textarea"
        placeholder="Share Your Detailed Experience...."
        value={reviewText}
        onChange={(e) => setReviewText(e.target.value)}
      ></textarea>

      {/* 📸 Photos */}
      <div className="club-review-photos">
        {photos.map((p, idx) => (
          <div key={idx} className="club-review-photo">
            <img src={p.preview} alt="review upload" />
            <button
              type="button"
              className="club-review-photo-remove"
              onClick={() => removePhoto(p.preview)}
            >
              <FaTimes />
            </button>
          </div>
        ))}
      </div>

      {/* 📤 Upload Button */}
      <label className="club-review-upload">
        <FaCamera /> Add Photos
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handlePhotoUpload}
          hidden
        />
      </label>

      {/* ✅ Buttons */}
      <div className="club-review-actions">
        <button
          type="button"
          className="club-review-cancel-btn"
          onClick={() => navigate(-1)}
        >
          Cancel
        </button>
        <button type="submit" className="club-review-submit-btn">
          Submit
        </button>
      </div>

      {responseMessage && (
        <p className="club-review-response">{responseMessage}</p>
      )}
    </form>
  );
}
